import { DeviceControls, DeviceHistory, GroupSettingsDeviceList } from 'components';
import BaseMap from 'components/BaseMap/BaseMap';
import BaseModal from 'components/Modals/BaseModal';
import TextToggle from 'components/UI/TextToggle';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
import {
  buildGroupCommandArgs,
  GROUP_HISTORY_PAGE_SIZE,
  GROUP_HISTORY_START_MS,
} from 'features/Integrations/features/ConnectedDeviceGroups/features/GroupSettingsModal/utils/groupSettingsModalUtils';
import AudioManager from 'features/Integrations/features/ConnectedDevices/features/AudioManager/AudioManager';
import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import { DeviceIsDeviceType } from 'features/Integrations/utils/deviceComparisionUtils';
import {
  defineGroupType,
  isDeviceTypeAllowed,
} from 'features/Integrations/utils/deviceGroupRulesUtils';
import VideoPlayer from 'features/VMS/features/VideoPlayer/index.web';
import L from 'leaflet';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Marker, Polyline } from 'react-leaflet';
import { sendGroupCommand } from 'services/devices/commands/commands.service';
import {
  useGetConnectedDevicesQuery,
  useKillswitchDeviceMutation,
} from 'services/devices/devices.api';
import { getDynamicDeviceIcon } from 'utils/devices/icon.utils';
import { notifyErrorToast } from 'utils/notifications/toast.utils';
import DevicePinPopup from './components/DevicePinPopup';

/**
 * @param {{group: object, onSelectDevice: (id: number), onClose?: ()}}
 */
export default function DeviceGroupSettings({ group, onSelectDevice }) {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [killswitchDevice, { isLoading: isKilling }] = useKillswitchDeviceMutation();

  const perms = usePerms();

  const groupDevices =
    connectedDevices?.filter((_device) => group?.devices?.includes(_device.id)) || [];
  const groupType = defineGroupType(group?.devices, connectedDevices, group?.type);
  const macroEligible =
    groupDevices.length > 0 &&
    groupDevices.every((device) => isDeviceTypeAllowed(device?.device_type, groupType));

  // The history table lists each member's command events over time, and the
  // page load holds ONE row for each device. Fetch the newest rows for the
  // members when this opens; they merge into the device cache the table reads.
  // See GROUP_HISTORY_START_MS for why the window starts at the epoch.
  const {
    isFetching: historyLoading,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
    error: historyError,
  } = useDeviceSeriesData({
    ids: groupDevices.map((device) => device.id),
    startTime: GROUP_HISTORY_START_MS,
    limit: GROUP_HISTORY_PAGE_SIZE,
  });

  const [avgPosition, setAvgPosition] = useState();
  const [virtualDevice, setVirtualDevice] = useState({ device_type: '' });
  const [showVideo, setShowVideo] = useState(false);
  const [sending, setSending] = useState(false);
  const [audioManager, setAudioManager] = useState(false);
  const [audioOverride, setAudioOverride] = useState(null);

  // Memoized so AudioManager doesn't re-derive its audio list (and refetch
  // every file) on every unrelated re-render, e.g. a resize.
  const paSirenIds = useMemo(
    () =>
      connectedDevices
        ?.filter(
          (_device) =>
            group.devices?.includes(_device.id) &&
            _device.device_type === 'Technology Bridge_Siren/Giant Voice' &&
            _device?.camera,
        )
        .map((_device) => _device.id) || [],
    [connectedDevices, group.devices],
  );

  //TODO: Figure out how to determine the current status of the group
  //TODO: Implement camera toggle on map

  useEffect(() => {
    const devices = groupDevices;

    if (devices.length < 1) {
      setAvgPosition({
        lat: 29.798707863118377,
        lng: -98.29839841752587,
      });
    } else {
      let avgPos = { lat: 0, lng: 0 };
      devices.forEach(
        (device) =>
          (avgPos = {
            lat: avgPos.lat + Number(device.location.lat),
            lng: avgPos.lng + Number(device.location.lng),
          }),
      );

      setAvgPosition({
        lat: avgPos.lat / devices.length,
        lng: avgPos.lng / devices.length,
      });
    }

    const selectedType = groupType || group?.type || '';
    const device = devices.find((device) => device.device_type === selectedType) || devices[0];

    const data = connectedDevices
      ?.filter(
        (_device) => group.devices?.includes(_device.id) && !DeviceIsDeviceType(_device, 'Stage'),
      )
      .map((_device) => _device?.data)
      .flatMap((v) => v)
      .sort((a, b) => b.ts - a.ts);

    setVirtualDevice({
      ...device,
      data: data,
      device_type: selectedType || device?.device_type || '',
    });
  }, [group, connectedDevices, groupDevices, groupType]);

  const typeToPermMap = {
    'Technology Bridge_Flashing Lights': 'RescAlertDevicesTechnologyBridge_FlashingLights',
    'Technology Bridge_Siren/Giant Voice': 'RescAlertDevicesTechnologyBridge_Siren-GiantVoice',
  };

  const createEditPermission = () => {
    if (!group.type) return true;

    const permissionType = typeToPermMap[group.type];
    if (!permissionType) return true;

    // A group command is its own permission (2026-08-28): SendDeviceGroupCommands.
    return perms.testPermission([], ['SendDeviceGroupCommands']);
  };

  const renderDevice = (deviceId, index) => {
    const device = connectedDevices?.find((device) => device.id === deviceId);

    if (device)
      return (
        <Marker
          key={deviceId + '' + index}
          position={device.location}
          icon={getDynamicDeviceIcon(device, 'icon')}
        >
          <DevicePinPopup device={device} onSelect={onSelectDevice} />
        </Marker>
      );
  };

  const renderLine = (line) => {
    return (
      <>
        <Polyline positions={line.coordinates} color={'#FF0000'} />

        {line.coordinates.map((point) => (
          <Marker
            position={point}
            icon={
              new L.DivIcon({
                className: 'custom-x-icon',
                html: `<div style="color: red; font-size: 30px; position: absolute; top: -4px; left: -2px;">X</div>`,
                iconSize: [20, 20],
                iconAnchor: [10, 16],
              })
            }
          />
        ))}
      </>
    );
  };

  const switchMaintenanceMode = async (device, kill_switchValue) => {
    if (!perms.isMaintenanceOperator()) return;

    try {
      await killswitchDevice({ id: device.id, state: kill_switchValue }).unwrap();
    } catch (err) {
      console.error('Error sending group command:', err.message);
      notifyErrorToast('maintenance mode', { isAddOperation: false });
    }
  };

  const groupSwitchMaintenanceMode = () => {
    const devices = getGroupDevices();
    //If any device has a kill switch enabled
    if (devices.some((device) => device.kill_switch === 1)) {
      //Disable all kill switches
      devices
        .filter((_device) => _device.kill_switch === 1) //Isolate only the enabled ones
        .map((_device) => switchMaintenanceMode(_device, 0));
    } else {
      //Enable all kill switches
      devices.map((_device) => switchMaintenanceMode(_device, 1));
    }
  };

  const getGroupDevices = () => {
    return groupDevices;
  };
  const videoPlayerLabel = `${group?.name || group?.alias || group?.id || 'Device group'} camera feed`;

  const deviceControlListener = async (evt, data) => {
    switch (evt) {
      case 'update':
        setVirtualDevice(data);
        return;
      case 'submit':
        try {
          setSending(true);
          // The group's file, volume and repeat now travel with the command (the backend used to drop them).
          await sendGroupCommand(group.id, data.status, data.comments, buildGroupCommandArgs(data));
          setSending(false);
        } catch (err) {
          console.error('Error sending device command:', err.message);
          setSending(false);
        }

        //Disable on close when command is sent.
        // onClose();
        return;
      case 'cancel-audio':
        try {
          await sendGroupCommand(group.id, 7, '');
        } catch (err) {
          console.error('Error sending device command:', err.message);
        }
        return;
      case 'audio-manager':
        setAudioManager(true);
        return;
      default:
        return;
    }
  };

  const onSetTempAudio = (fileName) => {
    setAudioOverride({
      id: -1,
      name: fileName,
      displayName: fileName,
      relay_id: 1,
      device_ids: null,
    });

    setAudioManager(false);
  };

  return (
    <div className="d-flex flex-column min-h-75vh gap-2 overflow-y-auto overflow-x-hidden">
      {/* Top */}
      <div className="d-flex flex-column flex-lg-row flex-fill flex-basis-0 gap-2">
        <div className="d-flex flex-column flex-fill flex-basis-0">
          {avgPosition && (
            <div className="d-flex flex-fill flex-column min-h-250">
              {!showVideo && (
                <div className="base-map-fill-wrapper">
                  <BaseMap
                    mapCenter={[avgPosition?.lat, avgPosition?.lng]}
                    zoom={10}
                    minZoom={0}
                    maxZoom={19}
                    scrollZoom
                    touchZoom
                    style={{ zIndex: 0 }}
                  >
                    {/* check for coordinates existing before mapping */}
                    {group?.coords && group?.coords.map(renderLine)}

                    {group?.devices && group.devices.map(renderDevice)}
                  </BaseMap>
                </div>
              )}

              {showVideo && group?.aux_devices?.length > 0 && (
                <div className="d-flex flex-fill">
                  <VideoPlayer
                    ariaLabel={videoPlayerLabel}
                    src={`${process.env.PUBLIC_API_URL}/v2/integrations/device/camera/${encodeURIComponent(group.aux_devices[0])}/video-stream/index.m3u8`}
                  />
                </div>
              )}

              {group?.aux_devices?.length > 0 &&
                perms.testPermission(
                  [8, 9, 10, 11, 12],
                  [`ViewExacqVisionOverwatchVMSConnections`],
                ) && (
                  <div className="mt-2">
                    <TextToggle
                      options={['Map', 'Video']}
                      value={showVideo}
                      setValue={setShowVideo}
                    />
                  </div>
                )}
            </div>
          )}

          {macroEligible &&
            perms.isMaintenanceOperator() &&
            layout[virtualDevice.device_type.toLowerCase()] && (
              <Button
                variant={`${
                  getGroupDevices().some(
                    (device) => device.kill_switch || device.data?.status === -1,
                  )
                    ? 'warning'
                    : 'primary'
                } mt-1 border border-black`}
                onClick={groupSwitchMaintenanceMode}
              >
                {getGroupDevices().some(
                  (device) => device.kill_switch || device.data?.status === -1,
                )
                  ? 'Disable Maintenance Mode'
                  : 'Enable Maintenance Mode'}
              </Button>
            )}
        </div>

        {macroEligible &&
          virtualDevice &&
          createEditPermission() &&
          layout[virtualDevice.device_type.toLowerCase()] && (
            <div className="flex-fill flex-basis-0">
              <DeviceControls
                device={virtualDevice}
                devices={getGroupDevices()}
                on={deviceControlListener}
                sending={sending}
                audioOverride={audioOverride}
              />
            </div>
          )}
      </div>

      {/* Bottom */}
      <div className="d-flex flex-column flex-lg-row flex-fill flex-basis-0 gap-2">
        {macroEligible && layout[virtualDevice.device_type.toLowerCase()] && (
          <div className="d-flex flex-fill flex-basis-0">
            <DeviceHistory
              title={'Group Device History'}
              data={virtualDevice?.data}
              deviceType={virtualDevice?.device_type}
              loading={historyLoading}
              hasMore={historyHasMore}
              onLoadMore={loadMoreHistory}
              error={historyError}
            />
          </div>
        )}

        <div className="d-flex flex-fill flex-basis-0">
          <GroupSettingsDeviceList
            devices={connectedDevices?.filter((_device) => group.devices?.includes(_device.id))}
            onSelect={onSelectDevice}
            highlightIds={
              virtualDevice?.audio?.device_ids ? [...virtualDevice.audio.device_ids] : []
            }
          />
        </div>
      </div>

      {/* Audio Manager */}
      <BaseModal
        show={perms.testPermission([], ['ViewSirenAudioFiles']) && audioManager}
        onHide={() => setAudioManager(false)}
        stackLevel={2}
      >
        <AudioManager
          deviceIds={paSirenIds}
          onClose={() => setAudioManager(false)}
          onPlayTemp={onSetTempAudio}
        />
      </BaseModal>
    </div>
  );
}
